import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SideNavbar from './helpers/SideNavbar';
import moment from 'moment-timezone';
import 'react-quill/dist/quill.snow.css';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faVideo, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';


const ViewPostedAiGeneratedSchedulePost = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [activeMedia, setActiveMedia] = useState(null);
    const ContentId = useParams("ContentId");
    const navigate = useNavigate();

    const styles = {
        pageCard: { border: 'none', backgroundColor: 'transparent' },
        headerBar: { borderRadius: '18px', padding: '18px 24px', backgroundColor: '#ffffff', border: '1px solid #eef0f8' },
        headerTitle: { fontSize: '22px', color: '#2e3192', fontWeight: 700, margin: 0 },
        gridRow: { marginTop: '4px' },
        card: { borderRadius: '22px', border: '1px solid #eef0f8', padding: '24px', backgroundColor: '#ffffff', minHeight: '100%', display: 'flex', flexDirection: 'column' },
        cardContent: { display: 'flex', flexDirection: 'column', gap: '20px', flexGrow: 1 },
        sectionTitle: { fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#858aad', fontWeight: 600, marginBottom: '8px' },
        scheduleHeading: { fontSize: '20px', color: '#2e3192', fontWeight: 700, marginBottom: '4px' },
        scheduleSubheading: { fontSize: '14px', color: '#6c7293', marginBottom: 0 },
        timezoneChip: { backgroundColor: '#eef0ff', color: '#2e3192', borderRadius: '999px', padding: '6px 12px', fontWeight: 600, fontSize: '12px', display: 'inline-flex', alignItems: 'center' },
        statusBadge: { backgroundColor: '#e9f8ff', color: '#0d6efd', borderRadius: '999px', padding: '6px 14px', fontWeight: 600, fontSize: '12px', display: 'inline-flex', alignItems: 'center', marginTop: '8px' },
        metaStack: { display: 'flex', flexDirection: 'column', gap: '8px' },
        mediaWrapper: {
            position: 'relative',
            borderRadius: '22px',
            background: 'linear-gradient(135deg, rgba(46,49,146,0.12) 0%, rgba(107,99,255,0.08) 100%)',
            padding: '18px',
            marginTop: '20px',
            overflow: 'hidden'
        },
        mediaHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
        mediaBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            fontWeight: 700,
            color: '#2e3192',
            backgroundColor: '#ffffff',
            borderRadius: '999px',
            padding: '8px 14px',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            boxShadow: '0 4px 12px rgba(46,49,146,0.15)'
        },
        mediaPreviewShell: {
            borderRadius: '18px',
            backgroundColor: '#fff',
            padding: '12px',
            boxShadow: '0 8px 24px rgba(46,49,146,0.12)',
            maxWidth: '400px',
            margin: '0 auto'
        },
        mediaPreview: {
            borderRadius: '14px',
            overflow: 'hidden',
            backgroundColor: '#0f172a',
            height: '150px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        mediaFooter: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '14px',
            color: '#4f5480',
            fontSize: '12px'
        },
        mediaFilename: { fontWeight: 600, wordBreak: 'break-all' },
        captionSection: { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: '12px' },
        contentBox: { backgroundColor: '#f8f9ff', borderRadius: '18px', padding: '18px', color: '#34385b', whiteSpace: 'pre-wrap', flexGrow: 1, overflowY: 'auto' },
        promptBox: { backgroundColor: '#ffffff', border: '1px dashed #d9dcff', borderRadius: '18px', padding: '18px', color: '#4f5480' },
        pollBox: { backgroundColor: '#ffffff', border: '1px solid #eef0f8', borderRadius: '18px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px' },
        pollQuestion: { fontWeight: 600, color: '#2e3192' },
        pollOption: { backgroundColor: '#f8f9ff', borderRadius: '12px', padding: '10px 14px', fontSize: '14px', color: '#4f5480' },
        toggleLink: { marginTop: '8px', fontWeight: 600, color: '#2e3192', cursor: 'pointer', border: 'none', background: 'transparent', padding: 0 },
    };

    const handleDate = (scheduledPostDate) => {
        // Check if the date exists
        if (!scheduledPostDate) {
            return { date: 'Invalid date' };
        }
        
        // If it's already a formatted date string (YYYY-MM-DD), just return it
        if (scheduledPostDate.match(/^\d{2}-\d{2}-\d{4}$/)) {
            return { date: scheduledPostDate };
        }
        
        // Split by 'T' and take the first part (date)
        const dateTimeString = scheduledPostDate.split('T')[0];
        
        // Parse the date string
        const dateTime = moment.utc(dateTimeString);
        
        // Check if the moment is valid
        if (!dateTime.isValid()) {
            return { date: 'Invalid date' };
        }
        
        // Format date
        const date = dateTime.format('MM-DD-YYYY');
    
        return { date };
    };

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                console.log('Fetching post with ContentId:', ContentId.ContentId);
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getallgeneratedpostsbyContentId/${ContentId.ContentId}`);
                console.log('Regular post API response:', response.data);
                
                // If no data returned, this might be a KnackFeeds post - try KnackFeeds API
                if (!response.data || response.data.length === 0 || (response.data.length === 1 && !response.data[0])) {
                    console.log('No data from regular post API, trying KnackFeeds API...');
                    try {
                        const knackFeedsResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getpost/${ContentId.ContentId}`);
                        console.log('KnackFeeds API response:', knackFeedsResponse.data);
                        if (knackFeedsResponse.data) {
                            // This is a KnackFeeds post - redirect to KnackFeeds view
                            console.log('Redirecting to KnackFeeds view...');
                            navigate(`/viewlatesttrendingpost/${ContentId.ContentId}`, { replace: true });
                            return;
                        }
                    } catch (knackFeedsErr) {
                        console.error('KnackFeeds API also failed:', knackFeedsErr.message);
                    }
                }
                
                setPosts(response.data);
                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch posts:', err.message);
                // If regular post API fails, try KnackFeeds API as fallback
                try {
                    console.log('Trying KnackFeeds API as fallback...');
                    const knackFeedsResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getpost/${ContentId.ContentId}`);
                    if (knackFeedsResponse.data) {
                        // This is a KnackFeeds post - redirect to KnackFeeds view
                        console.log('Redirecting to KnackFeeds view...');
                        navigate(`/viewlatesttrendingpost/${ContentId.ContentId}`, { replace: true });
                        return;
                    }
                } catch (knackFeedsErr) {
                    console.error('Both APIs failed:', knackFeedsErr.message);
                }
                setError('Failed to fetch posts');
                setLoading(false);
            }
        };
        fetchPosts();
    }, [ContentId.ContentId, navigate]);

    const handleTime = (value) => {
        if (!value) return 'Time not selected';
        if (moment.parseZone(value, moment.ISO_8601, true).isValid()) {
            return moment.parseZone(value, moment.ISO_8601, true).format('hh:mm A');
        }
        if (moment(value, 'HH:mm', true).isValid()) {
            return moment(value, 'HH:mm', true).format('hh:mm A');
        }
        // Try to extract time from ScheduledTimeAsPerZone format
        const timeStr = value.toString();
        const timeMatch = timeStr.match(/(\d{2}):(\d{2})/);
        if (timeMatch) {
            const hours = parseInt(timeMatch[1], 10);
            const minutes = parseInt(timeMatch[2], 10);
            const time = moment({ hours, minutes });
            return time.format('hh:mm A');
        }
        return value;
    };

    const resolveMediaDetails = (mediaPath = '') => {
        if (!mediaPath) return null;
        const normalized = `${mediaPath}`.trim().replace(/\\/g, '/');
        if (!normalized) return null;

        const isFullUrl = /^https?:\/\//i.test(normalized);
        const isAzureBlobUrl = normalized.includes('blob.core.windows.net');
        const isLinkedInAsset = normalized.includes('linkedin.com') || normalized.includes('media.licdn.com');

        const pathWithoutQuery = normalized.split('?')[0];
        const extension = pathWithoutQuery.split('.').pop()?.toLowerCase() || '';
        const filename = pathWithoutQuery.split(/[/\\]/).pop() || '';

        let mediaType = 'unknown';
        if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(extension)) {
            mediaType = 'video';
        } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension) || isLinkedInAsset) {
            mediaType = 'image';
        }

        const source = encodeURI(
            isFullUrl || isAzureBlobUrl || isLinkedInAsset
                ? normalized
                : `${process.env.REACT_APP_API_BASE_URL}/${normalized}`
        );

        return {
            source,
            mediaType,
            filename,
        };
    };

    const renderMediaPreview = (mediaDetails) => {
        if (!mediaDetails) {
            return (
                <div className="text-muted">
                    Media not available.
                </div>
            );
        }

        if (mediaDetails.mediaType === 'video') {
            return (
                <video controls style={{ width: '100%', height: '100%', maxHeight: '320px', borderRadius: '14px' }}>
                    <source src={mediaDetails.source} />
                    Your browser does not support the video tag.
                </video>
            );
        }

        if (mediaDetails.mediaType === 'image') {
            return (
                <img
                    src={mediaDetails.source}
                    alt="Post media"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    onError={(e) => {
                        e.currentTarget.style.display = 'none';
                    }}
                />
            );
        }

        return (
            <div className="text-muted">
                Preview not available for this file type.
            </div>
        );
    };

    const getMediaIcon = (mediaType) => (mediaType === 'video' ? faVideo : faImage);

    const getMediaLabel = (mediaType) => {
        if (mediaType === 'video') return 'Video Attachment';
        if (mediaType === 'image') return 'Image Attachment';
        return 'Media Attachment';
    };

    const getMediaInteractionHint = (mediaType) => {
        if (mediaType === 'video') return 'Tap to play';
        if (mediaType === 'image') return 'Tap to zoom';
        return 'View media';
    };

    const getMediaFilename = (mediaDetails) => (mediaDetails ? mediaDetails.filename : '');

    const handleMediaPreviewClick = (mediaDetails) => {
        if (!mediaDetails || mediaDetails.mediaType === 'unknown') return;
        setActiveMedia(mediaDetails);
        setShowMediaModal(true);
    };

    const handleCloseMediaModal = () => {
        setShowMediaModal(false);
        setActiveMedia(null);
    };

    const renderModalMedia = () => {
        if (!activeMedia) return null;

        if (activeMedia.mediaType === 'video') {
            return (
                <video controls style={{ width: '100%', borderRadius: '12px' }}>
                    <source src={activeMedia.source} />
                    Your browser does not support the video tag.
                </video>
            );
        }

        if (activeMedia.mediaType === 'image') {
            return (
                <img
                    src={activeMedia.source}
                    alt={activeMedia.filename || 'Preview'}
                    style={{ width: '100%', borderRadius: '12px', objectFit: 'contain' }}
                />
            );
        }

        return null;
    };

    const post = posts[0];
    const { date } = post ? handleDate(post.ScheduledPostDate || post.StartDate) : { date: 'Not scheduled' };
    const time = post ? handleTime(post.ScheduledTimeAsPerZone) : 'Time not selected';
    const mediaPath = post?.Image || post?.image || post?.ImageURL || post?.imageURL;
    const mediaDetails = resolveMediaDetails(mediaPath);
    const postContent = post?.Content || 'No content available.';

    return (
        <SideNavbar>
            <div className="card shadow-sm mt-3" style={styles.pageCard}>
                <div style={styles.headerBar} className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                    <Link
                        to="/viewallpostedposts"
                        className="text-decoration-none d-flex align-items-center gap-3"
                    >
                        <span className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 40, height: 40, backgroundColor: '#eef0ff' }}>
                            <i className="fa fa-arrow-left" aria-hidden="true" style={{ color: '#2e3192' }}></i>
                        </span>
                        <h5 className="welcome-text" style={styles.headerTitle}>
                            Linkedin Posts
                        </h5>
                      </Link> 
                </div>

                <div className="container-fluid" style={{ padding: '4px 16px' }}>
                    {error && (
                        <div className="alert alert-danger d-flex align-items-center gap-2 mb-4" role="alert">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <span>{error}</span>
                        </div>
                    )}
                {loading ? (
                        <div className="d-flex justify-content-center py-5">
                            <div className="spinner"></div>
                        </div>
                    ) : posts.length === 0 || !post ? (
                        <div className="text-center py-5">
                            <p style={{ color: '#2e3192', fontSize: '18px', fontWeight: 600, marginTop: '18px' }}>No post data available</p>
                            <p className="text-muted mb-0">The post you're looking for doesn't exist.</p>
                    </div>
                ) : (
                        <div className="row g-4" style={styles.gridRow}>
                            <div className="col-12">
                                <div className="shadow-sm h-100" style={styles.card}>
                                    <div style={styles.cardContent}>
                                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                                            <div style={styles.metaStack}>
                                                <span style={styles.sectionTitle}>Scheduled for</span>
                                                <div>
                                                    <div style={styles.scheduleHeading}>{date || 'Not scheduled'}</div>
                                                    <p style={styles.scheduleSubheading}>{time || 'Time not selected'}</p>
                                                </div>
                                            </div>
                                            <div className="text-end d-flex flex-column align-items-end gap-2">
                                                {post?.TimeZone && <span style={styles.timezoneChip}>{post.TimeZone}</span>}
                                                <span style={styles.statusBadge}>{post?.Status || 'POSTED'}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <div style={styles.sectionTitle}>Prompt</div>
                                            <div style={styles.promptBox}>{post?.Prompts || 'No prompt provided.'}</div>
                                        </div>

                                        {mediaDetails && (
                                            <div style={styles.mediaWrapper}>
                                                <div style={styles.mediaHeader}>
                                                    <span style={styles.mediaBadge}>
                                                        <FontAwesomeIcon
                                                            icon={getMediaIcon(mediaDetails.mediaType)}
                                                            style={{ fontSize: '16px' }}
                                                        />
                                                        {getMediaLabel(mediaDetails.mediaType)}
                                                    </span>
                                          </div>
                                                <div style={styles.mediaPreviewShell}>
                                                    <div
                                                        style={{
                                                            ...styles.mediaPreview,
                                                            cursor: mediaDetails.mediaType !== 'unknown' ? 'zoom-in' : 'default'
                                                        }}
                                                        role={mediaDetails.mediaType !== 'unknown' ? 'button' : undefined}
                                                        tabIndex={mediaDetails.mediaType !== 'unknown' ? 0 : undefined}
                                                        onClick={() => handleMediaPreviewClick(mediaDetails)}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter' || event.key === ' ') {
                                                                event.preventDefault();
                                                                handleMediaPreviewClick(mediaDetails);
                                                            }
                                                        }}
                                                    >
                                                        {renderMediaPreview(mediaDetails)}
                                                    </div>
                                                </div>
                                                <div style={styles.mediaFooter}>
                                                    <span>{getMediaInteractionHint(mediaDetails.mediaType)}</span>
                                                </div>
                                            </div>
                                        )}

                                        <div style={styles.captionSection}>
                                            <div style={styles.sectionTitle}>Post Caption</div>
                                            <div style={styles.contentBox}>
                                    {(() => {
                                                    // Check if this is a poll
                                                    const pollRequired = post?.pollrequired === true || post?.pollrequired === 1 || post?.pollrequired === 'true' || post?.pollrequired === '1';
                                                    const hasQuestionOptions = post?.QuestionwithOptions && 
                                                        (typeof post.QuestionwithOptions === 'string' ? post.QuestionwithOptions.trim() !== '' : post.QuestionwithOptions);
                                                    const isPoll = pollRequired || hasQuestionOptions;
                                                    
                                        let pollData = null;
                                                    if (isPoll && post?.QuestionwithOptions) {
                                                        try {
                                                            pollData = typeof post.QuestionwithOptions === 'string' 
                                                                ? JSON.parse(post.QuestionwithOptions) 
                                                                : post.QuestionwithOptions;
                                        } catch (e) {
                                          console.error('Error parsing poll data:', e);
                                                        }
                                        }
                                        
                                                    if (isPoll && pollData) {
                                          return (
                                                            <div>
                                                                <div style={{ marginBottom: '15px' }}>
                                                                    <div style={styles.pollQuestion}>{pollData.question || 'Poll Question'}</div>
                                              </div>
                                              {pollData.options && pollData.options.length > 0 && (
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                    {pollData.options.map((option, index) => (
                                                                            <div key={index} style={styles.pollOption}>
                                                                                {String.fromCharCode(65 + index)}. {option}
                                                        </div>
                                                    ))}
                                                  </div>
                                                                )}
                                                            </div>
                                                        );
                                                    }

                                                    return <div dangerouslySetInnerHTML={{ __html: postContent }}></div>;
                                    })()}   
                  </div>
                  </div>
                                       
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <Modal show={showMediaModal} onHide={handleCloseMediaModal} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Media Preview</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {renderModalMedia()}
                    </Modal.Body>
                </Modal>
            </div>
        </SideNavbar>
    );
};

export default ViewPostedAiGeneratedSchedulePost;
